import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, formatPrice } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const session_id = params.get('session_id');
    const [state, setState] = useState({ loading: true, status: null, tx: null, attempts: 0 });
    const { clear } = useCart();

    useEffect(() => {
        let cancelled = false;
        const poll = async (attempt = 0) => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/checkout/status/${session_id}`);
                if (cancelled) return;
                if (data.payment_status === 'paid') {
                    setState({ loading: false, status: 'paid', tx: data, attempts: attempt });
                    clear();
                    return;
                }
                if (data.status === 'expired') {
                    setState({ loading: false, status: 'expired', tx: data, attempts: attempt });
                    return;
                }
                if (attempt >= 8) {
                    setState({ loading: false, status: 'timeout', tx: data, attempts: attempt });
                    return;
                }
                setTimeout(() => poll(attempt + 1), 2500);
            } catch (e) {
                if (attempt >= 5) {
                    setState({ loading: false, status: 'error', tx: null, attempts: attempt });
                    return;
                }
                setTimeout(() => poll(attempt + 1), 2500);
            }
        };
        if (session_id) poll(0);
        else setState({ loading: false, status: 'missing', tx: null, attempts: 0 });
        return () => { cancelled = true; };
    }, [session_id, clear]);

    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="rrm-panel p-8 text-center" data-testid="checkout-success-panel">
                {state.loading && (
                    <>
                        <Loader2 className="w-8 h-8 mx-auto text-amber-400 animate-spin" />
                        <h1 className="mt-4 font-display text-3xl text-white">CONFIRMING PAYMENT…</h1>
                        <p className="text-white/60 mt-2">Hang tight, polling Stripe ({state.attempts + 1}/8).</p>
                    </>
                )}
                {!state.loading && state.status === 'paid' && (
                    <>
                        <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />
                        <h1 className="mt-4 font-display text-4xl text-white">ORDER CONFIRMED</h1>
                        <p className="text-white/70 mt-2">Thank you. Your order is paid and queued for fulfillment.</p>
                        {state.tx?.amount && (
                            <div className="mt-4 text-amber-400 font-display text-3xl">{formatPrice(state.tx.amount)}</div>
                        )}
                        <div className="text-xs text-white/40 mt-2 font-mono">{session_id}</div>
                        <div className="mt-6 flex flex-wrap justify-center gap-3">
                            <Link to="/shop" className="px-4 py-2 rounded-md bg-white/5 border border-white/10 text-sm">Keep shopping</Link>
                            <Link to="/account" className="px-4 py-2 rounded-md bg-[var(--rrm-red)] text-white text-sm">View orders</Link>
                        </div>
                    </>
                )}
                {!state.loading && state.status !== 'paid' && (
                    <>
                        <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" />
                        <h1 className="mt-4 font-display text-3xl text-white">PAYMENT NOT CONFIRMED YET</h1>
                        <p className="text-white/70 mt-2">Status: {state.status}. If your card was charged, the order will appear shortly.</p>
                        <Link to="/cart" className="inline-block mt-4 text-amber-300">Back to cart</Link>
                    </>
                )}
            </div>
        </div>
    );
}
